import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  API_BASE,
  GearDealsApiError,
  createGearDealClick,
  searchGearDeals,
} from "../api/gearDealsApi";
import { isUkCountry } from "../utils/ukAccess";
import "./GearDeals.css";

const SOURCE_OPTIONS = [
  { value: "all", labelKey: "gear_deals_filter_all_sources", fallback: "All sources" },
  { value: "ebay_uk", labelKey: "gear_deals_source_ebay_uk", fallback: "eBay UK" },
  {
    value: "total_fishing_tackle",
    labelKey: "gear_deals_source_total_fishing_tackle",
    fallback: "Total Fishing Tackle",
  },
  {
    value: "fishing_tackle_bait",
    labelKey: "gear_deals_source_fishing_tackle_bait",
    fallback: "Fishing Tackle & Bait",
  },
];

const CONDITION_OPTIONS = [
  { value: "all", labelKey: "gear_deals_filter_all_conditions", fallback: "All conditions" },
  { value: "new", labelKey: "gear_deals_condition_new", fallback: "New" },
  { value: "used", labelKey: "gear_deals_condition_used", fallback: "Used" },
  {
    value: "refurbished",
    labelKey: "gear_deals_condition_refurbished",
    fallback: "Refurbished",
  },
];

const SORT_OPTIONS = [
  { value: "best_match", labelKey: "gear_deals_sort_best_match", fallback: "Best match" },
  {
    value: "total_price_asc",
    labelKey: "gear_deals_sort_total_price",
    fallback: "Lowest estimated total",
  },
  {
    value: "item_price_asc",
    labelKey: "gear_deals_sort_item_price",
    fallback: "Lowest item price",
  },
];

function formatMoney(amount, currency = "GBP") {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return "N/A";
  }

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency || "GBP",
  }).format(Number(amount));
}

function formatDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsed);
}

function labelFromValue(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getGearDealsErrorMessage(error, t) {
  if (error instanceof GearDealsApiError && error.status === 403) {
    return t("gear_deals_blocked_title", {
      defaultValue: "Gear Deals Beta is available to UK users only.",
    });
  }

  if (error?.detail === "gear_deals_query_too_short") {
    return t("gear_deals_query_too_short", {
      defaultValue: "Enter at least 2 characters to search.",
    });
  }

  return t("gear_deals_error", {
    defaultValue: "Could not load gear deals right now.",
  });
}

function GearDealCard({
  result,
  query,
  searchEventId,
  onBlocked,
}) {
  const { t } = useTranslation();
  const [isOpening, setIsOpening] = useState(false);
  const [clickError, setClickError] = useState("");
  const lastChecked = formatDate(result.lastCheckedAt);

  const handleViewDeal = async () => {
    if (isOpening) return;
    setIsOpening(true);
    setClickError("");

    try {
      const payload = await createGearDealClick({
        provider: result.provider,
        offerId: result.offerId,
        searchEventId,
        query,
        sourcePage:
          typeof window !== "undefined" ? window.location.pathname : "/gear-deals",
      });

      const redirectPath = String(payload?.redirectPath || "");
      if (!redirectPath.startsWith("/gear-deals/r/")) {
        throw new Error("gear_deals_invalid_redirect_path");
      }

      if (typeof window !== "undefined") {
        window.location.assign(`${API_BASE}${redirectPath}`);
      }
    } catch (error) {
      if (error instanceof GearDealsApiError && error.status === 403) {
        onBlocked?.();
      }
      setClickError(
        t("gear_deals_click_error", {
          defaultValue: "Could not open this deal.",
        })
      );
      setIsOpening(false);
    }
  };

  return (
    <article className="gear-deals-card">
      {result.imageUrl ? (
        <img
          className="gear-deals-card__image"
          src={result.imageUrl}
          alt=""
          loading="lazy"
        />
      ) : (
        <div className="gear-deals-card__image-placeholder" aria-hidden="true">
          <span>GBP</span>
        </div>
      )}

      <div className="gear-deals-card__body">
        <div className="gear-deals-card__topline">
          <span>{result.retailer}</span>
          <span>{result.providerLabel}</span>
          <span className="gear-deals-demo-badge">
            {t("gear_deals_demo_badge", { defaultValue: "Demo data" })}
          </span>
        </div>
        <h2>{result.title}</h2>

        <div className="gear-deals-card__meta">
          <span>{labelFromValue(result.condition)}</span>
          <span>{labelFromValue(result.availability)}</span>
          {lastChecked ? (
            <span>
              {t("gear_deals_last_checked", {
                defaultValue: "Last checked {{date}}",
                date: lastChecked,
              })}
            </span>
          ) : null}
        </div>

        <dl className="gear-deals-prices">
          <div>
            <dt>
              {t("gear_deals_item_price", {
                defaultValue: "Item price",
              })}
            </dt>
            <dd>{formatMoney(result.priceAmount, result.priceCurrency)}</dd>
          </div>
          <div>
            <dt>
              {t("gear_deals_delivery", {
                defaultValue: "Delivery",
              })}
            </dt>
            <dd>{formatMoney(result.deliveryAmount, result.priceCurrency)}</dd>
          </div>
          <div>
            <dt>
              {t("gear_deals_total", {
                defaultValue: "Estimated total",
              })}
            </dt>
            <dd>{formatMoney(result.totalAmount, result.priceCurrency)}</dd>
          </div>
        </dl>

        <div className="gear-deals-card__footer">
          <span className={`gear-deals-match gear-deals-match--${result.matchLabel}`}>
            {t(`gear_deals_match_${result.matchLabel}`, {
              defaultValue: `${labelFromValue(result.matchLabel)} match`,
            })}{" "}
            / {result.matchConfidence}%
          </span>
          <button
            type="button"
            className="gear-deals-view-button"
            onClick={handleViewDeal}
            disabled={isOpening}
          >
            {isOpening
              ? t("gear_deals_opening", { defaultValue: "Opening..." })
              : t("gear_deals_check_retailer", {
                  defaultValue: "Check retailer",
                })}
          </button>
        </div>

        {clickError ? <div className="gear-deals-card__error">{clickError}</div> : null}
      </div>
    </article>
  );
}

export default function GearDeals({ currentUser }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [source, setSource] = useState("all");
  const [condition, setCondition] = useState("all");
  const [sort, setSort] = useState("best_match");
  const [results, setResults] = useState([]);
  const [searchEventId, setSearchEventId] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isBlocked, setIsBlocked] = useState(() => !isUkCountry(currentUser?.country));

  const canSearch = useMemo(() => query.trim().length >= 2, [query]);

  useEffect(() => {
    setIsBlocked(!isUkCountry(currentUser?.country));
  }, [currentUser?.country]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLoading) return;

    if (!isUkCountry(currentUser?.country)) {
      setIsBlocked(true);
      return;
    }

    if (!canSearch) {
      setErrorMessage(
        t("gear_deals_query_too_short", {
          defaultValue: "Enter at least 2 characters to search.",
        })
      );
      return;
    }

    setIsLoading(true);
    setErrorMessage("");
    setHasSearched(true);

    try {
      const payload = await searchGearDeals({
        query,
        source,
        condition,
        sort,
      });
      setSearchEventId(String(payload?.searchEventId || ""));
      setResults(Array.isArray(payload?.results) ? payload.results : []);
      setIsBlocked(false);
    } catch (error) {
      if (error instanceof GearDealsApiError && error.status === 403) {
        setIsBlocked(true);
      }
      setResults([]);
      setSearchEventId("");
      setErrorMessage(getGearDealsErrorMessage(error, t));
    } finally {
      setIsLoading(false);
    }
  };

  if (isBlocked) {
    return (
      <section className="gear-deals-page">
        <div className="gear-deals-state gear-deals-state--blocked">
          <h1>
            {t("gear_deals_blocked_title", {
              defaultValue: "Gear Deals Beta is available to UK users only.",
            })}
          </h1>
          <p>
            {t("gear_deals_blocked_body", {
              defaultValue:
                "This beta compares UK retailer prices and is limited to UK accounts while we test it.",
            })}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="gear-deals-page">
      <header className="gear-deals-header">
        <div>
          <h1>{t("gear_deals_title", { defaultValue: "Gear Deals Beta" })}</h1>
          <p>
            {t("gear_deals_subtitle", {
              defaultValue:
                "Compare fishing gear prices from UK sellers and find the best value before you buy.",
            })}
          </p>
        </div>
      </header>

      <form className="gear-deals-search" onSubmit={handleSubmit}>
        <label className="gear-deals-field gear-deals-field--search">
          <span>{t("gear_deals_search_label", { defaultValue: "Search" })}</span>
          <input
            type="search"
            value={query}
            maxLength={80}
            placeholder={t("gear_deals_search_placeholder", {
              defaultValue: "Search rods, reels, lures...",
            })}
            onChange={(event) => {
              setQuery(event.target.value);
              setErrorMessage("");
            }}
          />
        </label>

        <label className="gear-deals-field">
          <span>{t("gear_deals_source_filter", { defaultValue: "Source" })}</span>
          <select value={source} onChange={(event) => setSource(event.target.value)}>
            {SOURCE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.labelKey, { defaultValue: option.fallback })}
              </option>
            ))}
          </select>
        </label>

        <label className="gear-deals-field">
          <span>{t("gear_deals_condition_filter", { defaultValue: "Condition" })}</span>
          <select
            value={condition}
            onChange={(event) => setCondition(event.target.value)}
          >
            {CONDITION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.labelKey, { defaultValue: option.fallback })}
              </option>
            ))}
          </select>
        </label>

        <label className="gear-deals-field">
          <span>{t("gear_deals_sort_filter", { defaultValue: "Sort" })}</span>
          <select value={sort} onChange={(event) => setSort(event.target.value)}>
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.labelKey, { defaultValue: option.fallback })}
              </option>
            ))}
          </select>
        </label>

        <button type="submit" className="gear-deals-search__button" disabled={isLoading}>
          {isLoading
            ? t("gear_deals_searching", { defaultValue: "Searching..." })
            : t("gear_deals_search_button", { defaultValue: "Search" })}
        </button>
      </form>

      <p className="gear-deals-disclaimer">
        {t("gear_deals_disclaimer", {
          defaultValue:
            "Beta results use seeded demo offer data. Retailer links may open a search page until live provider APIs are connected. Always confirm the exact model, price, stock and delivery on the retailer site.",
        })}
      </p>

      {errorMessage ? <div className="gear-deals-alert">{errorMessage}</div> : null}

      {isLoading ? (
        <div className="gear-deals-state">
          {t("gear_deals_loading", { defaultValue: "Searching gear deals..." })}
        </div>
      ) : hasSearched && results.length === 0 && !errorMessage ? (
        <div className="gear-deals-state">
          <h2>
            {t("gear_deals_no_results_title", {
              defaultValue: "No matching deals found.",
            })}
          </h2>
          <p>
            {t("gear_deals_no_results_body", {
              defaultValue: "Try another brand, model, or item type.",
            })}
          </p>
        </div>
      ) : results.length > 0 ? (
        <div className="gear-deals-results">
          {results.map((result) => (
            <GearDealCard
              key={`${result.provider}-${result.offerId}`}
              result={result}
              query={query}
              searchEventId={searchEventId}
              onBlocked={() => setIsBlocked(true)}
            />
          ))}
        </div>
      ) : (
        <div className="gear-deals-state">
          {t("gear_deals_idle", {
            defaultValue: "Search UK gear deals from the seeded beta providers.",
          })}
        </div>
      )}
    </section>
  );
}
